import type { SyncResult } from "./sync-client";

export type SyncCoordinatorStatus = "idle" | "scheduled" | "syncing" | "synced" | "failed";

export type SyncCoordinatorSnapshot = {
  status: SyncCoordinatorStatus;
  failureCount: number;
  lastError?: string;
  lastSuccessAt?: string;
  lastResult?: SyncResult;
};

export type SyncScheduler = {
  setInterval(handler: () => void, delayMs: number): unknown;
  clearInterval(handle: unknown): void;
};

const defaultScheduler: SyncScheduler = {
  setInterval: (handler, delayMs) => globalThis.setInterval(handler, delayMs),
  clearInterval: (handle) => globalThis.clearInterval(handle as ReturnType<typeof globalThis.setInterval>),
};

export class SyncCoordinator {
  private timer: unknown;
  private inFlight: Promise<SyncResult> | undefined;
  private snapshot: SyncCoordinatorSnapshot = { status: "idle", failureCount: 0 };

  constructor(
    private readonly runner: () => Promise<SyncResult>,
    private readonly scheduler: SyncScheduler = defaultScheduler,
    private readonly onUpdate: (snapshot: SyncCoordinatorSnapshot) => void = () => {},
  ) {}

  getSnapshot(): SyncCoordinatorSnapshot {
    return { ...this.snapshot };
  }

  start(intervalSeconds: number): void {
    if (!Number.isInteger(intervalSeconds) || intervalSeconds < 15) throw new Error("Sync interval must be at least 15 seconds");
    this.stop();
    this.timer = this.scheduler.setInterval(() => { void this.trigger().catch(() => {}); }, intervalSeconds * 1000);
    this.update({ status: "scheduled" });
  }

  stop(): void {
    if (this.timer !== undefined) {
      this.scheduler.clearInterval(this.timer);
      this.timer = undefined;
    }
    if (this.snapshot.status !== "idle") this.update({ status: "idle" });
  }

  trigger(): Promise<SyncResult> {
    if (this.inFlight) return this.inFlight;
    this.update({ status: "syncing", lastError: undefined });
    this.inFlight = this.runner()
      .then((result) => {
        this.update({ status: "synced", failureCount: 0, lastSuccessAt: new Date().toISOString(), lastResult: result, lastError: undefined });
        return result;
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : "Sync failed";
        this.update({ status: "failed", failureCount: this.snapshot.failureCount + 1, lastError: message });
        throw error;
      })
      .finally(() => { this.inFlight = undefined; });
    return this.inFlight;
  }

  private update(patch: Partial<SyncCoordinatorSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    this.onUpdate(this.getSnapshot());
  }
}
